import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, Link2, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { ApplyQuestions } from "@/components/ApplyQuestions";
import { api, errMsg } from "@/lib/api";
import { Masked } from "@/components/Masked";
import { MobileRow } from "@/components/MobileRow";
import { CollapsibleCard } from "@/components/CollapsibleCard";

const th = "text-left px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500";
const td = "px-3 py-2 font-mono text-xs text-zinc-300 border-t border-zinc-900 align-top";
const STATUSES = ["new", "contacted", "booked", "onboarded", "rejected"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [calendly, setCalendly] = useState("");
  const [seats, setSeats] = useState({
    seats_total: 0,
    seats_taken: 0,
    seats_period: "",
    applicants_week: 0,
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, b] = await Promise.all([api.get("/admin/leads"), api.get("/admin/brand")]);
      setLeads(l.data);
      setCalendly(b.data.calendly_url || "");
      setSeats({
        seats_total: b.data.seats_total || 0,
        seats_taken: b.data.seats_taken || 0,
        seats_period: b.data.seats_period || "",
        applicants_week: b.data.applicants_week || 0,
      });
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveLink = async () => {
    setBusy(true);
    try {
      await api.put("/admin/brand", {
        calendly_url: calendly,
        ...seats,
        seats_total: Number(seats.seats_total) || 0,
        seats_taken: Number(seats.seats_taken) || 0,
        applicants_week: Number(seats.applicants_week) || 0,
      });
      toast.success("Landing page settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.lead_id}`, { status });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (lead) => {
    try {
      await api.delete(`/admin/leads/${lead.lead_id}`);
      toast.success("Lead removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Shell>
      <div className="space-y-5" data-testid="leads-page">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="p-2 text-zinc-500 hover:text-zinc-100 border border-zinc-800 rounded-sm transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-semibold tracking-tight">Desk Applications</h1>
            <p className="text-xs text-zinc-500 font-mono mt-0.5">
              {leads.length} application{leads.length === 1 ? "" : "s"} from strictrr.com
            </p>
          </div>
        </div>

        <CollapsibleCard title="1-1 call booking link &amp; seats" icon={CalendarClock}
                         storageKey="leads-booking" testid="leads-booking-card">
          <div className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="calendly-url-input"
              value={calendly}
              onChange={(e) => setCalendly(e.target.value)}
              placeholder="https://calendly.com/your-handle/30min"
              className="flex-1 rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2.5 font-mono text-sm text-zinc-100 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={saveLink}
              disabled={busy}
              data-testid="save-calendly-btn"
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs uppercase tracking-[0.1em] font-semibold rounded-sm transition-colors duration-200"
            >
              Save link
            </button>
          </div>
          <p className="text-[11px] text-zinc-600 font-mono">
            Shown right after someone completes the application on the marketing site. Leave blank
            to collect applications only.
          </p>

          <div className="pt-3 border-t border-zinc-900 space-y-3">
            <h3 className="text-sm font-heading font-semibold tracking-tight">
              Seat scarcity on the landing page
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                ["seats_total", "Total seats", "25", "number"],
                ["seats_taken", "Seats taken", "18", "number"],
                ["seats_period", "Period label", "this month", "text"],
                ["applicants_week", "Applied this week", "34", "number"],
              ].map(([k, lbl, ph, type]) => (
                <div key={k}>
                  <span className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-1.5">
                    {lbl}
                  </span>
                  <input
                    data-testid={`seat-${k}-input`}
                    type={type}
                    value={seats[k]}
                    placeholder={ph}
                    onChange={(e) => setSeats({ ...seats, [k]: e.target.value })}
                    className="w-full rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2.5 font-mono text-sm text-zinc-100 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-600 font-mono">
              Set total seats to 0 to hide every seat counter. These are your numbers — nothing is
              calculated from live capacity.
            </p>
            <button
              onClick={saveLink}
              disabled={busy}
              data-testid="save-seats-btn"
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs uppercase tracking-[0.1em] font-semibold rounded-sm transition-colors duration-200"
            >
              Save seats
            </button>
          </div>
          </div>
        </CollapsibleCard>

        <CollapsibleCard title="Application questions" storageKey="leads-questions"
                         testid="leads-questions-card">
          <ApplyQuestions />
        </CollapsibleCard>

        <CollapsibleCard
          title="Applications"
          count={leads.length}
          storageKey="leads-table"
          testid="leads-card"
        >
        <div className="md:hidden divide-y divide-zinc-800">
          {leads.length === 0 && (
            <p className="px-4 py-10 text-center text-xs text-zinc-600">No applications yet.</p>
          )}
          {leads.map((l) => (
            <MobileRow
              key={l.lead_id}
              testid={`lead-card-${l.email}`}
              title={
                <>
                  <Masked value={l.name} kind="name" testid={`lead-card-name-${l.lead_id}`}
                          className="text-xs text-zinc-100" />
                  <Masked value={l.email} kind="email" testid={`lead-card-email-${l.lead_id}`}
                          className="font-mono text-[10px] text-zinc-500" />
                </>
              }
              trailing={
                <span className="font-mono text-[10px] uppercase text-zinc-400 shrink-0">
                  {l.status}
                </span>
              }
              rows={[
                ["Contact", <Masked value={l.contact} kind="name"
                                    testid={`lead-card-contact-${l.lead_id}`} />],
                ["Account size", l.account_size || "—"],
                ["Experience", l.experience || "—"],
                ["Broker", [l.broker, l.instruments].filter(Boolean).join(" · ") || "—"],
                ["Call time", l.preferred_time || "—"],
                ["Struggle", l.struggle || "—"],
                l.notes ? ["Notes", l.notes] : null,
              ]}
              actions={
                <>
                  <select
                    value={l.status}
                    data-testid={`lead-card-status-${l.email}`}
                    onChange={(e) => setStatus(l, e.target.value)}
                    className="rounded-sm bg-zinc-900/60 border border-zinc-800 px-2 py-1.5 font-mono text-xs text-zinc-100 focus:border-blue-500 focus:outline-none"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <a
                    href={`mailto:${l.email}`}
                    className="p-1.5 text-zinc-500 hover:text-blue-400 transition-colors duration-200"
                    title="Email applicant"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                  </a>
                  <button
                    onClick={() => remove(l)}
                    data-testid={`lead-card-delete-${l.email}`}
                    className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors duration-200"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              }
            />
          ))}
        </div>

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr>
                <th className={th}>Applicant</th>
                <th className={th}>Profile</th>
                <th className={th}>Struggle</th>
                <th className={th}>Call time</th>
                <th className={th}>Status</th>
                <th className={th}></th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-xs text-zinc-600">
                    No applications yet.
                  </td>
                </tr>
              )}
              {leads.map((l) => (
                <tr key={l.lead_id} data-testid={`lead-row-${l.email}`}>
                  <td className={td}>
                    <Masked value={l.name} kind="name" testid={`lead-name-${l.lead_id}`}
                            className="text-zinc-100" />
                    <Masked value={l.email} kind="email" testid={`lead-email-${l.lead_id}`}
                            className="text-zinc-500" />
                    <Masked value={l.contact} kind="name" testid={`lead-contact-${l.lead_id}`}
                            className="text-zinc-600" />
                  </td>
                  <td className={td}>
                    <div>{l.account_size || "—"}</div>
                    <div className="text-zinc-500">{l.experience}</div>
                    <div className="text-zinc-600">
                      {[l.broker, l.instruments].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td className={`${td} max-w-[220px] whitespace-normal`}>
                    <div>{l.struggle || "—"}</div>
                    {l.notes && <div className="text-zinc-600 mt-1">{l.notes}</div>}
                  </td>
                  <td className={td}>{l.preferred_time || "—"}</td>
                  <td className={td}>
                    <select
                      value={l.status}
                      data-testid={`lead-status-${l.email}`}
                      onChange={(e) => setStatus(l, e.target.value)}
                      className="rounded-sm bg-zinc-900/60 border border-zinc-800 px-2 py-1.5 font-mono text-xs text-zinc-100 focus:border-blue-500 focus:outline-none"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <a
                        href={`mailto:${l.email}`}
                        className="p-1 text-zinc-600 hover:text-blue-400 transition-colors duration-200"
                        title="Email applicant"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                      </a>
                      <button
                        onClick={() => remove(l)}
                        data-testid={`lead-delete-${l.email}`}
                        className="p-1 text-zinc-600 hover:text-red-400 transition-colors duration-200"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </CollapsibleCard>
      </div>
    </Shell>
  );
}
